import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
} from "@mui/material";
import TableHeader from "../../layout/table/TableHeader";
import NoDataLabel from "../../layout/NoDataLabel";
import {format, parseISO} from "date-fns";
import {RRuleHelper} from "../../../../utils/RRuleHelper";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {useNavigate} from "react-router-dom";
import Constants from "../../../../utils/constants";
import ModalDelete from "../../layout/ModalDelete";
import Grid from "@mui/material/Grid2";

function TourneesGraffitiTable({tournees, loading, error, selectedClient, onClientChange, onDelete}) {
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('client');
    const [label, setLabel] = useState("");
    const [objectToDelete, setObjectToDelete] = useState(null);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const headCells = [
        {id: 'client', label: 'Client'},
        {id: 'libelle', label: 'Libellé'},
        {id: 'dateDebut', label: 'Début'},
        {id: 'rrule', label: 'Récurrence'},
        {id: 'operateur', label: 'Opérateur'},
        {id: 'active', label: "Active aujourd'hui"},
    ];

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const clients = useMemo(() => {
        const uniqueClients = new Set(tournees?.map(t => t.client?.libelle).filter(Boolean));
        return Array.from(uniqueClients).sort();
    }, [tournees]);

    const filtered = useMemo(() => {
        return tournees?.filter(t => (selectedClient === '' || t.client?.libelle === selectedClient));
    }, [tournees, selectedClient]);

    const sortedData = useMemo(() => {
        return filtered?.sort((a, b) => {
            if (orderBy === "client") {
                const aC = a.client ? a.client.libelle : '';
                const bC = b.client ? b.client.libelle : '';
                return order === 'asc' ? aC.localeCompare(bC) : bC.localeCompare(aC);
            }
            const av = a[orderBy];
            const bv = b[orderBy];
            const cmp = av > bv ? 1 : -1;
            return order === 'asc' ? cmp : -cmp;
        });
    }, [filtered, order, orderBy]);

    const openDeleteConfirmModal = (t) => {
        setLabel("Etes vous sûr de vouloir supprimer la tournée : ");
        setObjectToDelete(t.libelle);
        setOpen(true);
        setPendingToDelete(t);
    };

    const [pendingToDelete, setPendingToDelete] = useState(null);

    const handleClose = () => {
        setOpen(false);
        setPendingToDelete(null);
    };

    const handleDelete = () => {
        if (pendingToDelete && onDelete) {
            onDelete(pendingToDelete);
        }
        handleClose();
    };

    const navigateToEdit = (id) => {
        navigate(Constants.APP_ROUTE_ADMIN_TOURNEES_GRAFFITI_EDIT.replace(':id', id));
    };

    const isActiveToday = (dateDebut, rrule) => {
        if (dateDebut && rrule) {
            return RRuleHelper.isOccurrenceToday(dateDebut, rrule);
        }
        return false;
    };

    let currentClient = null;

    return (
        <React.Fragment>
            <Box sx={{mb: 2, mt: 5}}>
                <TextField
                    select
                    label="Filtrer par client"
                    value={selectedClient}
                    onChange={(e) => onClientChange && onClientChange(e.target.value)}
                    fullWidth
                    sx={{mb: 2}}
                >
                    <MenuItem value="">Tous les clients</MenuItem>
                    {clients.map((client) => (
                        <MenuItem key={client} value={client}>
                            {client}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
            <TableContainer component={Paper} className={"mt-2"} sx={{maxHeight: '75vh'}}>
                <Table aria-label="tournees graffiti table" size="small" stickyHeader>
                    <TableHeader headCells={headCells} order={order} orderBy={orderBy}
                                 onRequestSort={handleRequestSort}/>
                    <TableBody>
                        {sortedData?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align={"center"}>
                                    <NoDataLabel label={"Il n'y a encore aucune tournée graffiti enregistrée"}/>
                                </TableCell>
                            </TableRow>
                        )}
                        {sortedData && sortedData.map((t) => {
                            let addBorder = false;
                            if (currentClient !== t.client?.libelle) {
                                if (orderBy === "client") {
                                    addBorder = true;
                                    currentClient = t.client?.libelle;
                                }
                            }
                            return (
                                <TableRow key={t.id} hover className={addBorder ? 'problemTable-border' : ''}
                                          onClick={() => navigateToEdit(t.id)} style={{cursor: 'pointer'}}>
                                    <TableCell>
                                        <b>{t.client?.libelle}</b>
                                    </TableCell>
                                    <TableCell>
                                        {t.libelle}
                                    </TableCell>
                                    <TableCell>
                                        {t.dateDebut ? format(parseISO(t.dateDebut), 'dd/MM/yyyy') : ''}
                                    </TableCell>
                                    <TableCell>
                                        {RRuleHelper.rruleToText(t.rrule)}
                                    </TableCell>
                                    <TableCell>
                                        {t.chauffeur?.libelle} {t.equipier && <span> - {t.equipier.libelle}</span>}
                                    </TableCell>
                                    <TableCell>
                                        <Grid container size={12} justify={"flex-end"} spacing={2}>
                                            <Grid size={6}>
                                                {isActiveToday(t.dateDebut, t.rrule) ? (
                                                    <Button size="small" disabled className="toggle-button-small button-selected-green">Oui</Button>
                                                ) : (
                                                    <Button size="small" disabled className="toggle-button-small button-selected-red">Non</Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </TableCell>
																	<TableCell>
																		<Grid container size={12} justify={"flex-end"} spacing={2}>
																			<Grid size={3}>
																				<Button color="secondary" size="small"
																								onClick={(e) => {
																									e.stopPropagation();
																									navigateToEdit(t.id)
																								}}
																								startIcon={<ModeEditOutlineOutlinedIcon/>}>
																				</Button>
																			</Grid>
																			<Grid size={3}>
																				<Button color="error" size="small"
																								onClick={(e) => {
																									e.stopPropagation();
																									openDeleteConfirmModal(t)
																								}}
																								startIcon={<CloseOutlinedIcon/>}>
																				</Button>
																			</Grid>
																		</Grid>
																	</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <ModalDelete label={label} onClose={handleClose} open={open}
                         objectToDelete={objectToDelete}
                         selectedValue={pendingToDelete}
                         onDelete={handleDelete}/>
        </React.Fragment>
    );
}

export default TourneesGraffitiTable;
